/**
 * TransaccionesDelDia — cada cobro del día, con su cliente, su id en el PSP y
 * a qué compañía fue.
 *
 * EL NIVEL DONDE SE RESUELVE DE VERDAD (§5 del spec). Los agregados de arriba
 * dicen que hay un problema; esta lista dice cuál es la fila culpable.
 *
 * DOS ESTADOS POR FILA, SEPARADOS (R17): cobro y giro. Colapsarlos en un solo
 * pill esconde «cobrada pero no girada», que es justo el caso que importa.
 *
 * «ESPERANDO» NO ES «FALLIDA» (R18). Va en celeste —informativo— y fuera del
 * conteo de fallidas: pintarla de rojo manda a perseguir lo que corresponde
 * esperar.
 *
 * LOS FILTROS SE APLICAN EN CLIENTE (R19). Cada lectura del día cruza proxy +
 * payment + Clerk; filtrar no puede costar otra vuelta completa.
 *
 * UN FILTRO SIN RESULTADOS NO DICE «NO HUBO MOVIMIENTO» (R20). Son dos cosas
 * distintas y confundirlas hace cerrar un día creyendo que estuvo vacío.
 *
 * @see payment/src/services/transacciones-dia.service.ts — el reparto y los estados
 */
import React, { useMemo, useState } from 'react';

import { Badge, StatusPill } from './cierre/ds';
import { CIFRA } from '../../../lib/yoolpay/estados';
import { fmtClp, fmtHora, fmtNum } from '../../../lib/yoolpay/formato';

/** Estado del cobro → pill del DS. El color sale del componente, no de acá. */
const COBRO = {
  cobrada: { pill: 'activo', texto: 'Cobrada' },
  fallida: { pill: 'error', texto: 'Fallida' },
  // Celeste = informativo, esperando al PSP. NO rojo (R18).
  esperando: { pill: 'procesando', texto: 'Esperando' },
  cancelada: { pill: 'pausado', texto: 'Cancelada' },
};

const GIRO = {
  girado: { pill: 'activo', texto: 'Girado' },
  parcial: { pill: 'error', texto: 'Giro parcial' },
  sin_girar: { pill: 'pausado', texto: 'Sin girar' },
  informativo: { pill: 'procesando', texto: 'Pago propio' },
  no_aplica: { pill: 'procesando', texto: '—' },
};

const COLS = '58px 1fr 190px 150px 118px 116px 122px';

/**
 * La línea de negocio, en las palabras del negocio.
 *
 * `produccion` es la etiqueta del código; lo que el comercial entiende es «venta nueva».
 * `indeterminado` NO se pinta como ninguna de las dos: la cuota no dice de qué plan es, y
 * repartirla a ojo mueve plata de un informe a otro.
 */
const LINEA = {
  produccion: { texto: 'Venta nueva', variant: 'recomendado' },
  cobranza: { texto: 'Cobranza', variant: 'digital' },
  indeterminado: { texto: 'Sin clasificar', variant: 'pausado' },
};

/** `3,44%` — la tasa efectiva del PSP, para compararla con la pactada. */
const fmtTasa = (t) => (t == null ? '—' : `${(Number(t) * 100).toFixed(2).replace('.', ',')}%`);

const FILTROS_ESTADO = [
  { clave: 'todos', label: 'Todos' },
  { clave: 'cobrada', label: 'Cobradas' },
  { clave: 'sin_girar', label: 'Sin girar' },
  { clave: 'dividido', label: 'Divididos' },
  { clave: 'problema', label: 'Con alerta' },
];

/** ¿Pasa la fila el filtro de estado? */
function pasaEstado(f, filtro) {
  if (filtro === 'todos') return true;
  if (filtro === 'cobrada') return f.estado_cobro === 'cobrada';
  if (filtro === 'sin_girar') return f.estado_giro === 'sin_girar' || f.estado_giro === 'parcial';
  if (filtro === 'dividido') return f.dividido;
  // «CON ALERTA» ES TODO LO ACCIONABLE, y hasta el 2026-08-25 se le escapaba la mitad:
  // miraba sólo el cobro fallido y el giro parcial, y dejaba fuera las dos marcas del
  // control de comisión —el ✖ del dato roto y el ⚠ del desvío de tarifa—, que son
  // justamente las que mandan a hablar con el proveedor.
  if (filtro === 'problema') {
    return f.estado_cobro === 'fallida'
      || f.estado_giro === 'parcial'
      || (f.problemas ?? []).length > 0
      || f.desvio != null;
  }
  return true;
}

/** El monto de la fila. Una cuota sin cobrar muestra lo pactado, nunca `$0`. */
function montoDe(f) {
  if (f.monto_clp != null) return fmtClp(f.monto_clp);
  if (f.pactado?.monto == null) return 'sin valorizar';
  return f.pactado.moneda === 'CLP'
    ? fmtClp(f.pactado.monto)
    : `${fmtNum(f.pactado.monto)} ${f.pactado.moneda ?? ''}`.trim();
}

export default function TransaccionesDelDia({ transacciones }) {
  const [companiaId, setCompaniaId] = useState('todas');
  const [estado, setEstado] = useState('todos');

  // Memoizado y no `?? []` inline: un array nuevo en cada render invalida los dos
  // `useMemo` de abajo, que es exactamente el refetch de trabajo que R19 evita en la
  // red — con 500 filas, filtrar volvería a recorrerlas en cada tecla.
  const filas = useMemo(() => transacciones?.filas ?? [], [transacciones]);

  // Las compañías que de verdad aparecen ese día. Ofrecer un filtro por una
  // compañía sin transacciones sólo lleva a una lista vacía.
  const companias = useMemo(() => {
    const vistas = new Map();
    for (const f of filas) {
      for (const d of f.destinos) {
        if (!vistas.has(d.compania_id)) vistas.set(d.compania_id, d.compania_nombre ?? d.compania_id);
      }
    }
    return [...vistas].map(([id, nombre]) => ({ id, nombre }));
  }, [filas]);

  const visibles = useMemo(
    () => filas.filter(
      (f) => pasaEstado(f, estado)
        && (companiaId === 'todas' || f.destinos.some((d) => d.compania_id === companiaId)),
    ),
    [filas, estado, companiaId],
  );

  if (!transacciones) return null;

  const hayFiltro = estado !== 'todos' || companiaId !== 'todas';

  return (
    <div className="cw-card">
      <div style={{ padding: '19px 24px 15px', borderBottom: '1px solid var(--cw-linea)' }}>
        <div
          style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            gap: 20, flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 className="cw-card-title">Transacciones del día, una por una</h2>
            <p className="cw-card-sub">
              El nivel donde se resuelve de verdad: cada cobro con su cliente, su id en el
              PSP, a qué compañía fue y si ya salió en un giro. Los totales de arriba se
              explican acá — si algo no cuadra, la fila culpable está en esta lista.
            </p>
          </div>
          <span style={{ textAlign: 'right', flexShrink: 0 }}>
            <span className="cw-th" style={{ display: 'block' }}>Mostrando</span>
            <span
              className="cw-mono"
              style={{ display: 'block', fontSize: 20, fontWeight: 700, marginTop: 3 }}
            >
              {visibles.length === filas.length
                ? fmtNum(filas.length)
                : `${fmtNum(visibles.length)} de ${fmtNum(filas.length)}`}
            </span>
          </span>
        </div>

        <div
          style={{ display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span className="cw-th">Compañía</span>
            <span className="cw-segs">
              <button
                type="button"
                className={`cw-seg${companiaId === 'todas' ? ' on' : ''}`}
                onClick={() => setCompaniaId('todas')}
              >
                Todas
              </button>
              {companias.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`cw-seg${companiaId === c.id ? ' on' : ''}`}
                  onClick={() => setCompaniaId(c.id)}
                  title={c.id}
                >
                  {c.nombre}
                </button>
              ))}
            </span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span className="cw-th">Estado</span>
            <span className="cw-segs">
              {FILTROS_ESTADO.map((f) => (
                <button
                  key={f.clave}
                  type="button"
                  className={`cw-seg${estado === f.clave ? ' on' : ''}`}
                  onClick={() => setEstado(f.clave)}
                >
                  {f.label}
                </button>
              ))}
            </span>
          </span>
        </div>
      </div>

      {/* EL ENCABEZADO Y LAS FILAS VAN EN EL MISMO CONTENEDOR: si scrollean por separado
          las columnas se desalinean y el rótulo deja de corresponder a su cifra. El scroll
          vertical de las filas es aparte y sigue siendo suyo. */}
      <div className="cw-tabla">
      <div className="cw-row cw-head-row" style={{ gridTemplateColumns: COLS }}>
        <span className="cw-th">Hora</span>
        <span className="cw-th">Cliente y cargo</span>
        <span className="cw-th">Compañía destino</span>
        <span className="cw-th">Trx en el PSP</span>
        <span className="cw-th" style={{ textAlign: 'right' }}>Monto</span>
        <span className="cw-th">Cobro</span>
        <span className="cw-th">Giro</span>
      </div>

      {visibles.length === 0 ? (
        <div style={{ padding: '18px 24px' }}>
          {/* R20: «ninguna coincide con el filtro» y «no hubo movimiento» son dos
              cosas distintas, y confundirlas hace cerrar un día creyéndolo vacío. */}
          <span className="cw-aviso baja">
            <span className="cw-ai"><span>i</span></span>
            <span>
              {hayFiltro
                ? 'Ninguna transacción coincide con el filtro. Quita el filtro para ver las '
                  + `${fmtNum(filas.length)} del día.`
                : 'Este día no registró transacciones.'}
            </span>
          </span>
        </div>
      ) : (
        <div className="cw-scroll" style={{ maxHeight: 520, overflowY: 'auto' }}>
          {visibles.map((t, i) => {
            const c = COBRO[t.estado_cobro] ?? COBRO.esperando;
            const g = GIRO[t.estado_giro] ?? GIRO.no_aplica;
            const alerta = t.estado_giro === 'parcial' || t.estado_cobro === 'fallida';
            return (
              <div
                key={t.cargo_id ?? i}
                className={`cw-row${alerta ? ' obs' : ''}${i === visibles.length - 1 ? ' last' : ''}`}
                style={{ gridTemplateColumns: COLS, alignItems: 'flex-start' }}
              >
                <span
                  className="cw-mono"
                  style={{ fontSize: 12, color: 'var(--yool-muted)', paddingTop: 2 }}
                >
                  {fmtHora(t.procesado_at)}
                </span>

                <span style={{ minWidth: 0, paddingRight: 12 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-.01em' }}>
                      {t.cliente_nombre ?? 'Cliente sin nombre'}
                    </span>
                    {/* VENTA NUEVA O COBRANZA, en la fila. Antes había que cruzar contra
                        otra tabla para saber de qué línea era cada cobro. */}
                    {LINEA[t.linea] ? (
                      <Badge variant={LINEA[t.linea].variant} soft ring>
                        {LINEA[t.linea].texto}
                      </Badge>
                    ) : null}
                    {t.dividido ? (
                      <Badge variant="vence" soft ring>pago dividido</Badge>
                    ) : null}
                  </span>
                  <span
                    className="cw-mono"
                    style={{
                      display: 'block', fontSize: 11,
                      color: 'var(--cw-tinta-suave)', marginTop: 3,
                    }}
                  >
                    {t.suscripcion_codigo ?? t.cargo_id} · {t.plan ?? 'sin plan'}
                  </span>
                  {t.estado_giro === 'parcial' ? (
                    <span
                      style={{ display: 'block', fontSize: 11.5, color: CIFRA.rojo, marginTop: 4, lineHeight: 1.4 }}
                    >
                      El cobro se repartió y sólo una pata salió girada.
                    </span>
                  ) : null}

                  {/* ✖ EL DATO DEL PSP NO CUADRA. No depende de ninguna tarifa: si
                      `bruto − fee ≠ neto declarado`, está roto. Manda a revisar ESE cobro
                      con el proveedor, no el día entero. */}
                  {(t.problemas ?? []).map((p) => (
                    <span
                      key={p}
                      style={{
                        display: 'block', fontSize: 11.5, color: CIFRA.rojo,
                        marginTop: 4, lineHeight: 1.4,
                      }}
                    >
                      ✖ {p}
                    </span>
                  ))}

                  {/* ⚠ LA TASA EFECTIVA SE APARTA. NO es un error: la tarifa real puede
                      diferir de la pactada, y eso se conversa con el proveedor. Ámbar. */}
                  {t.desvio ? (
                    <span
                      style={{
                        display: 'block', fontSize: 11.5, color: CIFRA.ambar,
                        marginTop: 4, lineHeight: 1.4,
                      }}
                    >
                      ⚠ Tasa efectiva {fmtTasa(t.desvio.efectiva)} contra{' '}
                      {fmtTasa(t.desvio.configurada)} configurada.
                    </span>
                  ) : null}
                </span>

                {/* CADA DESTINO CON SU PARTE. Un cobro dividido muestra las dos, y
                    nunca el total a nombre de una sola (R11). */}
                <span style={{ minWidth: 0, paddingRight: 10 }}>
                  {t.destinos.map((d) => (
                    <span
                      key={d.compania_id}
                      style={{
                        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                        gap: 8, marginBottom: 3,
                      }}
                    >
                      <span
                        title={d.compania_id}
                        style={{
                          fontSize: 12.5, color: 'var(--yool-black)', whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                        }}
                      >
                        {d.compania_nombre ?? d.compania_id}
                      </span>
                      <span
                        className="cw-mono"
                        style={{ fontSize: 11.5, color: 'var(--yool-muted)', flexShrink: 0 }}
                      >
                        {fmtClp(d.monto_clp)}
                      </span>
                    </span>
                  ))}
                </span>

                {/* El id completo en el `title`: es lo que se pega en un ticket al
                    proveedor, así que se trunca pero no se oculta (R21). */}
                <span
                  className="cw-mono"
                  title={t.psp_charge_id ?? ''}
                  style={{
                    fontSize: 11.5, color: 'var(--yool-gray1)', paddingTop: 2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {t.psp_charge_id ?? '—'}
                </span>

                <span
                  className="cw-mono"
                  style={{ fontSize: 13, textAlign: 'right', fontWeight: 600, paddingTop: 1 }}
                >
                  {montoDe(t)}
                </span>

                <span><StatusPill estado={c.pill}>{c.texto}</StatusPill></span>
                <span><StatusPill estado={g.pill}>{g.texto}</StatusPill></span>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
